import {
	AlertTriangle,
	ArrowLeft,
	CheckCircle2,
	ChevronRight,
	CircleAlert,
	Clock,
	Lock,
	Plus,
	Search,
	Siren,
	UserCheck,
} from 'lucide-react'
import { type ReactNode, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { Button } from '@/components/occurrences/button'
import {
	PhotoPlaceholder,
	PotentialPill,
	ProgressBar,
	StatusBadge,
} from '@/components/occurrences/occurrence-ui'
import { PrototypeBar } from '@/components/occurrences/prototype-bar'
import { Input } from '@/components/ui/input'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { ROUTES } from '@/constants/routes'
import { cn } from '@/lib/utils'
import {
	BRANCHES,
	CLASSIFICATIONS,
	PERSONA,
	POTENTIAL_SHORT,
} from '@/mocks/occurrences/catalog'
import {
	can,
	canSeeOccurrence,
	canViewConfidentiality,
	getTransitions,
	isActionOverdue,
	progressPercent,
} from '@/mocks/occurrences/rules'
import { useOccurrenceMock } from '@/mocks/occurrences/store'
import type {
	ClassificationCode,
	MockProfile,
	Occurrence,
	OccurrenceType,
	Potential,
} from '@/types/occurrence'
import { TYPE_LABEL } from '@/types/occurrence'

type Tab = 'OPEN' | 'CLOSED' | 'ALL'
type Quick = 'NONE' | 'MINE' | 'OVERDUE' | 'HIGH'
type Sort = 'PRIORITY' | 'RECENT'

/** Cor da borda = potencial (gravidade), para a cor ter significado. */
const POTENTIAL_STRIPE: Record<Potential, string> = {
	N1: 'border-l-rose-600',
	N2: 'border-l-orange-500',
	N3: 'border-l-yellow-500',
	N4: 'border-l-teal-600',
	N5: 'border-l-teal-400',
}

const isFinished = (o: Occurrence) =>
	o.status === 'CLOSED' || o.status === 'CANCELLED'

const classificationOf = (o: Occurrence) =>
	o.classification ?? o.proposedClassification

const overdueCount = (o: Occurrence) =>
	o.actions.filter((a) => isActionOverdue(a.dueDate, a.status)).length

/** Situação da ocorrência para o perfil atual: o que falta ou quem precisa agir. */
function nextStepOf(o: Occurrence, profile: MockProfile) {
	if (o.status === 'CLOSED') return { tone: 'done' as const, text: 'Encerrada' }
	if (o.status === 'CANCELLED')
		return { tone: 'muted' as const, text: 'Cancelada' }
	const t = getTransitions(o, profile).find((x) => x.variant === 'primary')
	if (!t) return { tone: 'muted' as const, text: '' }
	if (t.deniedByProfile)
		return { tone: 'wait' as const, text: `Aguardando ${t.who}` }
	if (t.blockers.length > 0)
		return {
			tone: 'todo' as const,
			text:
				t.blockers.length === 1
					? `Falta: ${t.blockers[0].text}`
					: `Falta: ${t.blockers[0].text} e mais ${t.blockers.length - 1}`,
		}
	return { tone: 'ready' as const, text: `Pronta: ${t.label.toLowerCase()}` }
}

/** "Precisa de você": o próximo passo é do seu perfil, ou há ação sua para executar/aprovar. */
function needsMe(o: Occurrence, profile: MockProfile, userName: string) {
	if (isFinished(o)) return false
	const t = getTransitions(o, profile).find((x) => x.variant === 'primary')
	if (t && !t.deniedByProfile) return true
	return o.actions.some(
		(a) =>
			(a.responsible === userName &&
				['OPEN', 'IN_PROGRESS', 'REJECTED'].includes(a.status)) ||
			(a.approver === userName && a.status === 'AWAITING_APPROVAL'),
	)
}

const SummaryTile = ({
	active,
	icon,
	value,
	title,
	hint,
	tone,
	onClick,
}: {
	active: boolean
	icon: ReactNode
	value: number
	title: string
	hint: string
	tone: string
	onClick: () => void
}) => (
	<button
		type="button"
		onClick={onClick}
		aria-pressed={active}
		className={cn(
			'flex items-start gap-3 rounded-xl border bg-card p-3 text-left shadow-sm transition-all hover:shadow-md sm:p-4',
			active && 'border-kaefer-dark-grey ring-2 ring-kaefer-dark-grey/15',
		)}
	>
		<span
			className={cn(
				'hidden size-10 shrink-0 items-center justify-center rounded-lg sm:flex',
				tone,
			)}
		>
			{icon}
		</span>
		<span className="min-w-0">
			<span className="block text-2xl leading-tight font-bold text-kaefer-dark-grey tabular-nums">
				{value}
			</span>
			<span className="block text-sm font-medium text-kaefer-dark-grey">
				{title}
			</span>
			<span className="hidden text-xs text-muted-foreground sm:block">
				{hint}
			</span>
		</span>
	</button>
)

const STEP_TONE = {
	done: 'text-kaefer-forest-green',
	muted: 'text-muted-foreground',
	wait: 'text-muted-foreground',
	todo: 'text-kaefer-pumpkin-orange',
	ready: 'text-kaefer-forest-green',
}

const StepIcon = ({ tone }: { tone: keyof typeof STEP_TONE }) => {
	const cls = 'size-3.5 shrink-0'
	if (tone === 'done' || tone === 'ready')
		return <CheckCircle2 className={cls} />
	if (tone === 'wait') return <Lock className={cls} />
	if (tone === 'todo') return <CircleAlert className={cls} />
	return null
}

export const OccurrencesListPage = () => {
	const navigate = useNavigate()
	const { occurrences, profile } = useOccurrenceMock()
	const userName = PERSONA[profile].name

	const [tab, setTab] = useState<Tab>('OPEN')
	const [quick, setQuick] = useState<Quick>('NONE')
	const [search, setSearch] = useState('')
	const [cls, setCls] = useState<ClassificationCode | ''>('')
	const [branch, setBranch] = useState('')
	const [type, setType] = useState<OccurrenceType | ''>('')
	const [potential, setPotential] = useState<Potential | ''>('')
	const [sort, setSort] = useState<Sort>('PRIORITY')

	const visible = useMemo(
		() => occurrences.filter((o) => canSeeOccurrence(profile, o, userName)),
		[occurrences, profile, userName],
	)

	const open = visible.filter((o) => !isFinished(o))
	const stats = {
		mine: visible.filter((o) => needsMe(o, profile, userName)).length,
		open: open.length,
		overdue: visible.reduce((n, o) => n + overdueCount(o), 0),
		high: open.filter((o) => ['N1', 'N2', 'N3'].includes(o.potential)).length,
	}

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase()
		return visible
			.filter((o) =>
				tab === 'ALL' ? true : tab === 'OPEN' ? !isFinished(o) : isFinished(o),
			)
			.filter((o) => {
				if (quick === 'MINE') return needsMe(o, profile, userName)
				if (quick === 'OVERDUE') return overdueCount(o) > 0
				if (quick === 'HIGH')
					return !isFinished(o) && ['N1', 'N2', 'N3'].includes(o.potential)
				return true
			})
			.filter((o) => !cls || classificationOf(o) === cls)
			.filter((o) => !branch || o.branch === branch)
			.filter((o) => !type || o.type === type)
			.filter((o) => !potential || o.potential === potential)
			.filter((o) => {
				if (!q) return true
				return [
					String(o.number),
					o.description,
					o.employee?.name ?? '',
					o.contract,
					o.branch,
				]
					.join(' ')
					.toLowerCase()
					.includes(q)
			})
			.sort((a, b) =>
				sort === 'PRIORITY'
					? a.potential.localeCompare(b.potential) ||
						b.date.localeCompare(a.date)
					: b.date.localeCompare(a.date) || b.number - a.number,
			)
	}, [
		visible,
		tab,
		quick,
		search,
		cls,
		branch,
		type,
		potential,
		sort,
		profile,
		userName,
	])

	const toggleQuick = (q: Quick) => {
		setQuick((cur) => (cur === q ? 'NONE' : q))
		// os atalhos olham o que está em aberto (ações vencidas podem estar em encerradas)
		if (q !== 'OVERDUE') setTab('OPEN')
		else setTab('ALL')
	}

	const hasFilters =
		quick !== 'NONE' || search || cls || branch || type || potential
	const clearFilters = () => {
		setQuick('NONE')
		setSearch('')
		setCls('')
		setBranch('')
		setType('')
		setPotential('')
	}

	const TABS: { key: Tab; label: string; count: number }[] = [
		{ key: 'OPEN', label: 'Em andamento', count: open.length },
		{
			key: 'CLOSED',
			label: 'Encerradas e canceladas',
			count: visible.length - open.length,
		},
		{ key: 'ALL', label: 'Todas', count: visible.length },
	]

	return (
		<div className="flex flex-1 flex-col">
			<PrototypeBar />
			<div className="space-y-5 px-4 py-5 sm:px-6">
				{/* Título */}
				<div className="flex flex-wrap items-start justify-between gap-3">
					<div className="flex items-start gap-3">
						<Button
							variant="ghost"
							size="icon"
							className="mt-1 size-9"
							onClick={() => navigate(ROUTES.DASHBOARD.HOME)}
						>
							<ArrowLeft className="size-5" />
						</Button>
						<div>
							<h1 className="text-2xl font-semibold text-kaefer-dark-grey sm:text-3xl">
								Ocorrências
							</h1>
							<p className="mt-1 text-sm text-muted-foreground">
								Acidentes, incidentes, desvios de SSMA, RNC e desvios de
								governança · {visible.length} no seu escopo
							</p>
						</div>
					</div>
					{can(profile, 'create') && (
						<Button
							variant="primary"
							className="gap-2 font-semibold"
							onClick={() => navigate(ROUTES.OCCURRENCES.NEW)}
						>
							<Plus className="size-4" /> Nova ocorrência
						</Button>
					)}
				</div>

				{/* Resumo que também filtra */}
				<div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
					<SummaryTile
						active={quick === 'MINE'}
						onClick={() => toggleQuick('MINE')}
						icon={<UserCheck className="size-5" />}
						tone="bg-kaefer-honey-yellow/20 text-kaefer-pumpkin-orange"
						value={stats.mine}
						title="Precisam de você"
						hint="O próximo passo é do seu perfil ou há ação sua"
					/>
					<SummaryTile
						active={quick === 'NONE' && tab === 'OPEN'}
						onClick={() => {
							setQuick('NONE')
							setTab('OPEN')
						}}
						icon={<Clock className="size-5" />}
						tone="bg-kaefer-sapphire-blue/15 text-kaefer-sapphire-blue"
						value={stats.open}
						title="Em andamento"
						hint="Registradas e ainda não encerradas"
					/>
					<SummaryTile
						active={quick === 'OVERDUE'}
						onClick={() => toggleQuick('OVERDUE')}
						icon={<AlertTriangle className="size-5" />}
						tone="bg-kaefer-red/15 text-kaefer-red"
						value={stats.overdue}
						title="Ações vencidas"
						hint="Ações do plano com prazo expirado"
					/>
					<SummaryTile
						active={quick === 'HIGH'}
						onClick={() => toggleQuick('HIGH')}
						icon={<Siren className="size-5" />}
						tone="bg-orange-100 text-orange-600"
						value={stats.high}
						title="Potencial alto em aberto"
						hint="N1 a N3: com potencial de fatalidade"
					/>
				</div>

				{/* Abas + filtros */}
				<div className="space-y-3 rounded-xl border bg-card p-3 shadow-sm">
					<div className="flex flex-wrap items-center justify-between gap-2">
						<div className="flex max-w-full gap-1 overflow-x-auto rounded-lg bg-muted p-1">
							{TABS.map((t) => (
								<button
									type="button"
									key={t.key}
									onClick={() => setTab(t.key)}
									className={cn(
										'shrink-0 rounded-md px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors',
										tab === t.key
											? 'bg-card text-kaefer-dark-grey shadow-sm'
											: 'text-muted-foreground hover:text-kaefer-dark-grey',
									)}
								>
									{t.label}{' '}
									<span className="text-xs tabular-nums opacity-70">
										{t.count}
									</span>
								</button>
							))}
						</div>
						<div className="flex items-center gap-2 text-sm text-muted-foreground">
							Ordenar
							<NativeSelect
								value={sort}
								onChange={(e) => setSort(e.target.value as Sort)}
							>
								<NativeSelectOption value="PRIORITY">
									Maior potencial primeiro
								</NativeSelectOption>
								<NativeSelectOption value="RECENT">
									Mais recentes primeiro
								</NativeSelectOption>
							</NativeSelect>
						</div>
					</div>
					<div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-6">
						<div className="relative lg:col-span-2">
							<Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
							<Input
								className="pl-9"
								placeholder="Nº, descrição, colaborador, contrato..."
								value={search}
								onChange={(e) => setSearch(e.target.value)}
							/>
						</div>
						<NativeSelect
							value={potential}
							onChange={(e) => setPotential(e.target.value as Potential | '')}
						>
							<NativeSelectOption value="">
								Qualquer potencial
							</NativeSelectOption>
							{(['N1', 'N2', 'N3', 'N4', 'N5'] as Potential[]).map((p) => (
								<NativeSelectOption key={p} value={p}>
									{p} · {POTENTIAL_SHORT[p]}
								</NativeSelectOption>
							))}
						</NativeSelect>
						<NativeSelect
							value={cls}
							onChange={(e) =>
								setCls(e.target.value as ClassificationCode | '')
							}
						>
							<NativeSelectOption value="">
								Qualquer classificação
							</NativeSelectOption>
							{(Object.keys(CLASSIFICATIONS) as ClassificationCode[]).map(
								(c) => (
									<NativeSelectOption key={c} value={c}>
										{CLASSIFICATIONS[c].label}
									</NativeSelectOption>
								),
							)}
						</NativeSelect>
						<NativeSelect
							value={type}
							onChange={(e) => setType(e.target.value as OccurrenceType | '')}
						>
							<NativeSelectOption value="">Qualquer tipo</NativeSelectOption>
							{(Object.keys(TYPE_LABEL) as OccurrenceType[]).map((t) => (
								<NativeSelectOption key={t} value={t}>
									{TYPE_LABEL[t]}
								</NativeSelectOption>
							))}
						</NativeSelect>
						<NativeSelect
							value={branch}
							onChange={(e) => setBranch(e.target.value)}
						>
							<NativeSelectOption value="">Todas as filiais</NativeSelectOption>
							{BRANCHES.map((b) => (
								<NativeSelectOption key={b} value={b}>
									{b}
								</NativeSelectOption>
							))}
						</NativeSelect>
					</div>
					{hasFilters && (
						<div className="flex items-center justify-between text-xs text-muted-foreground">
							<span>{filtered.length} resultado(s) com os filtros atuais</span>
							<button
								type="button"
								onClick={clearFilters}
								className="font-medium text-kaefer-sapphire-blue hover:underline"
							>
								Limpar filtros
							</button>
						</div>
					)}
				</div>

				{/* Lista */}
				{filtered.length === 0 ? (
					<div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-kaefer-light-silver py-16">
						<AlertTriangle className="size-10 text-kaefer-silver" />
						<p className="mt-3 text-sm text-muted-foreground">
							Nenhuma ocorrência encontrada.
						</p>
					</div>
				) : (
					<div className="overflow-hidden rounded-xl border bg-card shadow-sm">
						{/* Cabeçalho das colunas (desktop) */}
						<div className="hidden grid-cols-[56px_minmax(0,2.4fr)_minmax(0,1.3fr)_minmax(0,1.6fr)_minmax(0,1fr)_20px] gap-4 border-b bg-muted/40 py-2 pr-4 pl-5 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase lg:grid">
							<span />
							<span>Ocorrência</span>
							<span>Potencial e classificação</span>
							<span>Situação</span>
							<span>Andamento</span>
							<span />
						</div>
						<ul className="divide-y">
							{filtered.map((o) => {
								const photo = o.evidences.find(
									(e) =>
										!e.deletedReason &&
										canViewConfidentiality(profile, e.confidentiality),
								)
								const code = classificationOf(o)
								const step = nextStepOf(o, profile)
								const overdue = overdueCount(o)
								const roots = o.investigation.causes.filter(
									(c) => c.isRoot,
								).length
								return (
									<li key={o.id}>
										<button
											type="button"
											onClick={() => navigate(`/occurrences/${o.id}`)}
											className={cn(
												'grid w-full grid-cols-[56px_minmax(0,1fr)] gap-x-4 gap-y-2 border-l-4 py-3 pr-4 pl-4 text-left transition-colors hover:bg-muted/40',
												'lg:grid-cols-[56px_minmax(0,2.4fr)_minmax(0,1.3fr)_minmax(0,1.6fr)_minmax(0,1fr)_20px] lg:items-center',
												POTENTIAL_STRIPE[o.potential],
											)}
										>
											{/* Miniatura */}
											{photo ? (
												<PhotoPlaceholder
													tone={photo.tone}
													className="size-14 rounded-lg lg:row-span-1"
												/>
											) : (
												<span className="flex size-14 items-center justify-center rounded-lg bg-muted text-[10px] text-muted-foreground">
													sem foto
												</span>
											)}

											{/* Ocorrência */}
											<div className="min-w-0">
												<p className="text-xs text-muted-foreground">
													<span className="font-semibold text-kaefer-dark-grey">
														#{o.number}
													</span>{' '}
													· {o.date.split('-').reverse().join('/')} ·{' '}
													{TYPE_LABEL[o.type]}
												</p>
												<p className="line-clamp-2 text-sm font-medium text-kaefer-dark-grey">
													{o.description || (
														<em className="font-normal text-muted-foreground">
															Sem descrição
														</em>
													)}
												</p>
												<p className="truncate text-xs text-muted-foreground">
													{[o.branch, o.contract, o.employee?.name]
														.filter(Boolean)
														.join(' · ') || 'Sem filial'}
												</p>
											</div>

											{/* Potencial e classificação */}
											<div className="col-start-2 min-w-0 space-y-1 lg:col-start-auto">
												<div className="flex items-center gap-1.5">
													<PotentialPill value={o.potential} />
													<span className="truncate text-xs text-kaefer-dark-grey">
														{POTENTIAL_SHORT[o.potential]}
													</span>
												</div>
												<p className="truncate text-xs text-muted-foreground">
													{code ? (
														<>
															{CLASSIFICATIONS[code].label}
															{!o.classification && ' (proposta)'}
														</>
													) : (
														'Sem classificação'
													)}
												</p>
											</div>

											{/* Situação */}
											<div className="col-start-2 min-w-0 space-y-1 lg:col-start-auto">
												<StatusBadge status={o.status} />
												{step.text && step.tone !== 'done' && (
													<p
														className={cn(
															'flex items-center gap-1 text-xs',
															STEP_TONE[step.tone],
														)}
													>
														<StepIcon tone={step.tone} />
														<span className="truncate">{step.text}</span>
													</p>
												)}
											</div>

											{/* Andamento */}
											<div className="col-start-2 min-w-0 space-y-1 lg:col-start-auto">
												<ProgressBar value={progressPercent(o)} />
												<p className="text-[11px] text-muted-foreground">
													{roots} causa(s)-raiz · {o.actions.length} ação(ões)
													{overdue > 0 && (
														<span className="font-semibold text-kaefer-red">
															{' '}
															· {overdue} vencida(s)
														</span>
													)}
												</p>
											</div>

											<ChevronRight className="hidden size-4 text-muted-foreground lg:block" />
										</button>
									</li>
								)
							})}
						</ul>
					</div>
				)}

				{/* Legenda da cor */}
				<p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
					<span className="font-medium">Cor da borda = potencial:</span>
					{(['N1', 'N2', 'N3', 'N4', 'N5'] as Potential[]).map((p) => (
						<span key={p} className="flex items-center gap-1.5">
							<span
								className={cn(
									'h-3 w-1 rounded-full border-l-4',
									POTENTIAL_STRIPE[p],
								)}
							/>
							{p} {POTENTIAL_SHORT[p]}
						</span>
					))}
				</p>
			</div>
		</div>
	)
}
